/**
 * Centralized entity-type string constants and predicates.
 *
 * Every consumer that needs to reason about specific entity types (decide
 * whether to render a person-flavoured avatar, etc.) MUST import from this
 * module — never re-declare the string literal inline or define another
 * per-feature `EMAIL_RECIPIENT_TYPES` / `PERSON_TYPES` / etc. Set.
 *
 * The motivation is mechanical: every time we add a new entity type that
 * belongs to one of these conceptual buckets (e.g. another person-kinded
 * type), this is the only file that needs to change. Tests in
 * `entityTypes.test.ts` lock in the invariants (Set contents, union
 * membership) so a stray inline literal can't silently drift.
 */

import type { EntityTypeName } from './types';

// ---------------------------------------------------------------------------
// Canonical type strings
// ---------------------------------------------------------------------------

export const CONTACT = 'ahoy_contact' satisfies EntityTypeName;
export const USER = 'ahoy_user' satisfies EntityTypeName;
export const COMPANY = 'ahoy_company' satisfies EntityTypeName;
export const DEAL = 'ahoy_deal' satisfies EntityTypeName;
export const MEETING = 'ahoy_meeting' satisfies EntityTypeName;
export const EMAIL_THREAD = 'ahoy_email_thread' satisfies EntityTypeName;
export const BOOKING = 'ahoy_booking' satisfies EntityTypeName;
export const MEETING_NOTES = 'ahoy_meeting_notes' satisfies EntityTypeName;
export const FILE = 'ahoy_file' satisfies EntityTypeName;
export const EVENT_AUTOMATION =
  'ahoy_event_automation' satisfies EntityTypeName;
export const SCHEDULED_AUTOMATION =
  'ahoy_scheduled_automation' satisfies EntityTypeName;
export const BOOKING_TYPE = 'ahoy_booking_type' satisfies EntityTypeName;
export const RESEARCH = 'ahoy_research' satisfies EntityTypeName;
export const NOTE = 'ahoy_note' satisfies EntityTypeName;
export const TASK = 'ahoy_task' satisfies EntityTypeName;

// ---------------------------------------------------------------------------
// Conceptual groupings
// ---------------------------------------------------------------------------

/**
 * Person-kinded entity types. The backend `person` kind has these
 * concrete materializations; any UI that special-cases "this is a person"
 * (avatar fallback to initials, Gravatar lookup, draft-email affordance,
 * etc.) should reference this set rather than enumerating inline.
 *
 * Sets are typed against `string` (not `EntityTypeName`) so callers can
 * test arbitrary strings (incoming entity_type_name values) without an
 * extra cast. The contents are still constrained to EntityTypeName via
 * the `satisfies` checks on each constant above.
 */
export const PERSON_TYPES: ReadonlySet<string> = new Set<string>([
  CONTACT,
  USER,
]);

/**
 * Entity types that have an image-based avatar (profile photo, Gravatar, or
 * favicon) and therefore fall back to *initials* — not the entity-type glyph —
 * when no image resolves. Every other type renders its type glyph as the
 * fallback (see `CrmObjectAvatar`'s `IconAvatar` path).
 *
 * This is the single source of truth for that distinction: `CrmObjectAvatar`
 * (CRM list primary column) and the `record` `EntityBadge` variant (report
 * grouped-entity column) both consume it so the two draw the identical picture.
 * Persons (contact, user) are a subset; the only non-person image-backed type
 * is `ahoy_company` (favicon-from-domain).
 */
export const IMAGE_AVATAR_TYPES: ReadonlySet<string> = new Set<string>([
  USER,
  CONTACT,
  COMPANY,
]);

/**
 * Person-kinded entity types that can be email recipients. Excludes
 * `ahoy_user` (org members aren't drafted-to via Draft email — they're
 * the org's outbound senders, not recipients).
 */
export const EMAIL_RECIPIENT_TYPES: ReadonlySet<string> = new Set<string>([
  CONTACT,
]);

/**
 * Entity types whose CRM data is unrestricted (resolved read access = `Allow`)
 * for org members by default, so the WebSocket delivers their updates WITHOUT
 * an explicit subscription. These are the shared directory / pipeline entities
 * every member can read in full.
 *
 * This gates the slideout fetch-skip optimization, and it is deliberately an
 * allowlist (skip only when PROVEN unrestricted) rather than a denylist of
 * known-restricted types. A denylist silently misses any scoped type it hasn't
 * enumerated — `ahoy_booking`, `ahoy_external_integration`, or a permission-
 * scoped custom `org_*` type — and would wrongly skip their fetch, leaving the
 * slideout to render stale data (the server pushes no WS updates for a scoped
 * type without a `useRestrictedSubscription`). Being ABSENT from this set is
 * fail-safe: the worst case is a redundant fetch, never stale data.
 *
 * Mirrors the backend restriction check (`_is_restricted_type_async` in
 * apps/crm_events/consumers.py): a type is restricted when resolved read access
 * is anything other than `Allow`. The frontend can't resolve per-user access,
 * so it treats only the always-`Allow`-by-default core types as unrestricted.
 */
export const SUBSCRIPTION_UNRESTRICTED_ENTITY_TYPES: ReadonlySet<string> =
  new Set<string>([CONTACT, USER, COMPANY, DEAL]);

/**
 * Restricted CRM entity types the calendar surface renders and needs live WS
 * updates for. Passed to useRestrictedSubscription from CalendarPage.
 * NOTE: the calendar also renders `ahoy_reminder` (and Recall sessions, which
 * arrive as `ahoy_meeting`), but per issue #10012 Key Decision 2 only meetings
 * and bookings are subscribed here; reminders keep fetch-on-open freshness.
 */
export const CALENDAR_RESTRICTED_ENTITY_TYPES: EntityTypeName[] = [
  MEETING,
  BOOKING,
];

/**
 * Restricted CRM entity types the record surfaces (SingleSlideout +
 * CRMObjectFullPageView) render via their timeline + sections/tabs, and need
 * live WS updates for. Shared by both record surfaces to avoid drift.
 */
// Tasks are record-restricted so their live updates are delivered on record surfaces.
export const RECORD_RESTRICTED_ENTITY_TYPES: EntityTypeName[] = [
  MEETING,
  MEETING_NOTES,
  EMAIL_THREAD,
  NOTE,
  FILE,
  TASK,
];

/**
 * Restricted CRM entity types the dashboard renders and needs live WS updates
 * for. Passed to useRestrictedSubscription from DashboardHome.
 *
 * Without this the dashboard received NO live CRM updates at all: `ahoy_meeting`
 * is restricted (resolved read access is Scoped, not Allow), and the consumer
 * drops restricted-type events for any socket that has not explicitly
 * subscribed (`crm_events_batch` in apps/crm_events/consumers.py filters on
 * subscription state BEFORE the access-control filter). The socket connected and
 * heartbeated normally, which made the gap invisible — the server simply
 * withheld every meeting event.
 *
 * MEETING carries the meeting-side `ahoy_association_added` the summary CTA
 * depends on (the engine emits the reverse edge as its own event on the
 * meeting). MEETING_NOTES brings the notes object itself, so the target body
 * lands in the store rather than waiting on the debounced related-query refetch.
 *
 * Reminders are deliberately NOT subscribed, matching the calendar's decision
 * (issue #10012 Key Decision 2): they keep fetch-on-open freshness.
 */
export const DASHBOARD_RESTRICTED_ENTITY_TYPES: EntityTypeName[] = [
  MEETING,
  MEETING_NOTES,
];

/**
 * Entity types that have a hover-preview body component
 * (`hoverBodyDispatch.tsx`). Other types render as bare badges with no
 * popover. Keep in sync with the dispatch branches in
 * `hoverBodyDispatch.tsx` — a type listed here without a matching
 * dispatch branch produces an empty popover.
 */
export const HOVER_PREVIEW_TYPES: ReadonlySet<string> = new Set<string>([
  CONTACT,
  COMPANY,
]);

// ---------------------------------------------------------------------------
// Predicates
// ---------------------------------------------------------------------------

/**
 * Convenience predicate. Use this rather than `type === CONTACT || type
 * === USER` — avoids drift when a new person-kinded type is added.
 */
export function isPersonType(type: string | undefined): boolean {
  return type !== undefined && PERSON_TYPES.has(type);
}

/**
 * Convenience predicate. Use this rather than `type === CONTACT`.
 */
export function isEmailRecipientType(type: string | undefined): boolean {
  return type !== undefined && EMAIL_RECIPIENT_TYPES.has(type);
}
